import {useCallback, useEffect, useRef, useState} from 'react';

type LocalStorageOptions<T> = {
  queryUpdate?: any;
  queryOnly?: string[];
  listenToSamePage?: boolean;
  listenToOtherPage?: boolean;
  storeImmediately?: boolean;
  _parse?: (v: string) => T;
  _stringify?: (v: T) => string;
};
const samePageListeners: Record<string, (() => void)[]> = {};
export function getLocalStorage<T>(key: string, defaultState: T, _parse: (v: string) => T = JSON.parse): T {
  const string = localStorage.getItem(key);
  try {
    if (string) return _parse(string);
  } catch (error) {
    console.warn('getLocalStorage', string);
    console.warn(error);
  }
  return defaultState;
}
export function useLocalStorage<T>(
  _key: string,
  _defaultValue: T,
  _options: LocalStorageOptions<T> = {}
): [T, (newValue: T) => void, (newValue: T) => void] {
  // ref props
  const [_, _rerender] = useState(0);
  const _props = {
    key: _key,
    defaultValue: _defaultValue,
    options: _options,
    _parse: _options._parse ?? JSON.parse,
    _stringify: _options._stringify ?? JSON.stringify,
    rerender: () => _rerender((v) => v + 1),
  };
  const state = useRef({
    ..._props,
    prevKey: null as string | null,
    value: _defaultValue,
  });
  state.current = {
    ...state.current,
    ..._props,
  };
  // get state
  const reparseValue = useCallback(() => {
    const {key, defaultValue, _parse} = state.current;
    state.current.prevKey = key;
    const newValue = getLocalStorage(key, defaultValue, _parse);
    const {queryOnly, queryUpdate} = state.current.options;
    (queryOnly ?? []).forEach((k) => {
      newValue[k] = queryUpdate[k];
    });
    state.current.value = newValue;
  }, []);
  if (state.current.key !== state.current.prevKey) {
    reparseValue();
    if (_options.storeImmediately) {
      const {key, _stringify, value} = state.current;
      localStorage.setItem(key, _stringify(value));
    }
  }
  // queryUpdate
  const {queryUpdate} = _options;
  const prevQueryUpdate = useRef(null as any);
  if (queryUpdate && queryUpdate !== prevQueryUpdate.current) {
    const newValue = {
      ...state.current.value,
      ...queryUpdate,
    };
    state.current.value = newValue;
    const {key, _stringify} = state.current;
    localStorage.setItem(key, _stringify(newValue));
    prevQueryUpdate.current = queryUpdate;
  }
  // set state
  const setState = useCallback((newValue: T) => {
    state.current.value = newValue;
    const {key, _stringify} = state.current;
    localStorage.setItem(key, _stringify(newValue));
    (samePageListeners[key] ?? []).forEach((callback) => callback());
  }, []);
  const setStateAndRerender = useCallback(
    (newState: T) => {
      setState(newState);
      const {rerender} = state.current;
      rerender();
    },
    [setState]
  );
  // listen to other pages
  useEffect(() => {
    const callback = (event: StorageEvent) => {
      const {key} = state.current;
      if (event.key === key) {
        reparseValue();
        const {rerender} = state.current;
        rerender();
      }
    };
    if (_options.listenToOtherPage) {
      window.addEventListener('storage', callback);
      return () => {
        window.removeEventListener('storage', callback);
      };
    }
    return () => {
      // noop
    };
  }, [reparseValue, _options.listenToOtherPage]);
  // listen to same page
  useEffect(() => {
    const callback = () => {
      reparseValue();
      const {rerender} = state.current;
      rerender();
    };
    if (_options.listenToSamePage) {
      samePageListeners[_key] = samePageListeners[_key] ?? [];
      samePageListeners[_key].push(callback);
      return () => {
        const i = samePageListeners[_key].indexOf(callback);
        samePageListeners[_key].splice(i, 1);
      };
    }
    return () => {
      // noop
    };
  }, [reparseValue, _key, _options.listenToSamePage]);
  // return
  return [state.current.value, setState, setStateAndRerender];
}
type AnnotatedValue = {t: undefined | 'r' | 'D' | 'A' | 'S' | 'O'; v: unknown};
function _parseTagged(annotatedValue: AnnotatedValue): any {
  switch (annotatedValue.t) {
    case 'r':
      return annotatedValue.v;
    case 'D':
      return new Date(annotatedValue.v as string);
    case 'A':
      return (annotatedValue.v as AnnotatedValue[]).map(_parseTagged);
    case 'S':
      return new Set((annotatedValue.v as AnnotatedValue[]).map(_parseTagged));
    case 'O':
      return Object.fromEntries((annotatedValue.v as [string, AnnotatedValue][]).map(([k, v]) => [k, _parseTagged(v)]));
    default:
      return undefined;
  }
}
function parseTagged(string: string): any {
  return _parseTagged(JSON.parse(string));
}
function stringifyTagged(value: any): string {
  if (value === undefined) return '{}';
  if (value === null) return '{"t":"r", "v":null}';
  switch (value.constructor.name) {
    case 'Boolean':
    case 'Number':
      return `{"t":"r", "v":${value}}`;
    case 'String':
      return `{"t":"r", "v":"${value}"}`;
    case 'Date':
      return `{"t":"D", "v":"${(value as unknown as Date).toISOString()}"}`;
    case 'Set':
      return `{"t":"S", "v":[${Array.from((value as unknown as Set<unknown>).values()).map(stringifyTagged)}]}`;
    case 'Array':
      return `{"t":"A", "v":[${(value as unknown as unknown[]).map(stringifyTagged)}]}`;
    default: {
      const entries = Object.entries(value);
      return `{"t":"O", "v":[${entries.map(([k, v]) => `["${k}", ${stringifyTagged(v)}]`)}]}`;
    }
  }
}
export function getLocalStorageTagged<T>(key: string, defaultState: T): T {
  return getLocalStorage<T>(key, defaultState, parseTagged);
}
export function useLocalStorageTagged<T>(key: string, defaultState: T, options: LocalStorageOptions<T> = {}) {
  return useLocalStorage<T>(key, defaultState, {
    ...options,
    _parse: parseTagged,
    _stringify: stringifyTagged,
  });
}
// NOTE: this is called when user logs out manually, and in wallaby-infrastructure repo when user is logged out automatically
export function cleanLocalStorage(prefix = '/') {
  const keys = Object.keys(localStorage);
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    if (key.startsWith(prefix)) {
      localStorage.removeItem(key);
    }
  }
}
